import { FormEvent, useEffect, useMemo, useState } from 'react';
import OnelivePageLayout from './OnelivePageLayout';
import OneliveSubPageLayout from './OneliveSubPageLayout';
import {
  cacheItemShopOrderItem,
  fetchItemShopItem,
  fetchItemShopWallet,
  formatAmount,
  getCurrentUser,
  itemShopCharge,
  itemShopPurchase,
  navigate,
  readCachedItemShopOrderItemForPage,
  resolveItemPurchaseBlock,
  resolveRealName,
  resolveShopChargeAmount,
  resolveShopOrderItemId,
  showOneliveAlert,
  type ItemShopItem,
} from './api';

import { oneliveAssets } from './assets';

type PayType = 'bank' | 'coin';

interface Props {
  itemId?: string | number;
  chargeAmount?: string | number;
}

function formatNow(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

export default function OneliveItemShopOrderPage({ itemId, chargeAmount }: Props) {
  const parsedItemId = resolveShopOrderItemId(itemId);
  const parsedChargeAmount = resolveShopChargeAmount(chargeAmount);
  const isChargeMode = !parsedItemId && parsedChargeAmount > 0;
  const initialCachedItem = !isChargeMode ? readCachedItemShopOrderItemForPage(parsedItemId) : null;

  const [item, setItem] = useState<ItemShopItem | null>(initialCachedItem);
  const [depositorName, setDepositorName] = useState('');
  const [payType, setPayType] = useState<PayType>('bank');
  const [coinBalance, setCoinBalance] = useState<number | null>(null);
  const depositAt = useMemo(() => formatNow(), []);
  const [loading, setLoading] = useState(
    !isChargeMode && (!initialCachedItem || !initialCachedItem.description),
  );
  const [submitting, setSubmitting] = useState(false);

  const user = getCurrentUser();
  const supportsCoinPayment = !isChargeMode && (item?.supports_coin_payment || (item?.deduct_coins ?? 0) > 0);

  useEffect(() => {
    const defaultName = resolveRealName(user);
    if (defaultName) setDepositorName(defaultName);
  }, [user]);

  useEffect(() => {
    if (isChargeMode) return;
    if (!parsedItemId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      setLoading(true);
      const cached = readCachedItemShopOrderItemForPage(parsedItemId);
      if (cached && mounted) {
        setItem(cached);
      }

      const detail = await fetchItemShopItem(parsedItemId);
      if (!mounted) return;
      const nextItem = detail ?? cached;
      setItem(nextItem);
      if (nextItem) cacheItemShopOrderItem(nextItem);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [isChargeMode, parsedItemId]);

  const purchaseBlockReason = useMemo(() => {
    if (isChargeMode || !item) return null;
    return resolveItemPurchaseBlock(item, user?.member_level ?? 2);
  }, [isChargeMode, item, user?.member_level]);

  useEffect(() => {
    if (!purchaseBlockReason) return;
    showOneliveAlert(purchaseBlockReason, () => navigate('/shop'));
  }, [purchaseBlockReason]);

  useEffect(() => {
    if (!supportsCoinPayment) return;

    let mounted = true;
    (async () => {
      const wallet = await fetchItemShopWallet();
      if (!mounted) return;
      setCoinBalance(wallet?.coin_balance ?? 0);
    })();

    return () => { mounted = false; };
  }, [supportsCoinPayment]);

  const paymentGuide = useMemo(() => {
    if (isChargeMode) return '계좌이체 및 무통장입금만 가능합니다.';
    if (payType === 'coin' && supportsCoinPayment) {
      return item?.payment_guide_coin || '보유하신 코인으로 구매가능 합니다.';
    }
    return item?.payment_guide || '계좌이체 및 무통장입금만 가능합니다.';
  }, [isChargeMode, item?.payment_guide, item?.payment_guide_coin, payType, supportsCoinPayment]);

  const productDescription = useMemo(() => {
    if (isChargeMode) return '';
    if (payType === 'coin' && supportsCoinPayment) {
      return item?.description_coin || '';
    }
    return item?.description || '';
  }, [isChargeMode, item?.description, item?.description_coin, payType, supportsCoinPayment]);

  const title = isChargeMode ? '코인 충전' : '아이템 구매';
  const bankAmount = isChargeMode ? parsedChargeAmount : (item?.price ?? 0);
  const coinPayAmount = item?.deduct_coins ?? 0;
  const coinAmount = isChargeMode ? parsedChargeAmount : (item?.coin ?? 0);
  const productName = isChargeMode ? '코인 충전' : (item?.name ?? '');
  const isCoinPayMode = supportsCoinPayment && payType === 'coin';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!isChargeMode && payType === 'bank' && !depositorName.trim()) {
      showOneliveAlert('입금자명을 입력하세요.');
      return;
    }

    if (isChargeMode) {
      if (!Number.isFinite(parsedChargeAmount) || parsedChargeAmount < 1000) {
        showOneliveAlert('충전 금액은 최소 1,000원 이상이어야 합니다.');
        return;
      }
    } else if (!item) {
      showOneliveAlert('상품 정보를 불러올 수 없습니다.');
      return;
    } else {
      const blockReason = resolveItemPurchaseBlock(item, user?.member_level ?? 2);
      if (blockReason) {
        showOneliveAlert(blockReason);
        return;
      }

      if (payType === 'coin' && supportsCoinPayment) {
        if (coinBalance != null && coinBalance < coinPayAmount) {
          showOneliveAlert(`보유 코인이 부족합니다. (필요: ${formatAmount(coinPayAmount)}개)`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const result = isChargeMode
        ? await itemShopCharge(parsedChargeAmount, depositorName)
        : await itemShopPurchase(parsedItemId, depositorName, payType);

      const message = result.message
        || (isChargeMode
          ? `${formatAmount(parsedChargeAmount)}원 충전 주문이 접수되었습니다. 입금 확인 후 코인이 지급됩니다.`
          : payType === 'coin'
            ? `${result.item_name ?? productName} 구매가 완료되었습니다.`
            : `${result.item_name ?? productName} 주문이 접수되었습니다. 입금 확인 후 코인이 지급됩니다.`);

      showOneliveAlert(message, () => navigate('/shop'));
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || '주문 처리 중 오류가 발생했습니다.';
      showOneliveAlert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isChargeMode && parsedItemId <= 0 && !loading) {
    return (
      <OnelivePageLayout pageClassName="onelive-item-shop-page onelive-item-shop-order-page">
        <OneliveSubPageLayout title={title}>
          <p className="onelive-sub-empty">상품을 찾을 수 없습니다.</p>
          <button type="button" className="btn btn-info" onClick={() => navigate('/shop')}>
            아이템샵으로
          </button>
        </OneliveSubPageLayout>
      </OnelivePageLayout>
    );
  }

  if (!isChargeMode && !loading && !item) {
    return (
      <OnelivePageLayout pageClassName="onelive-item-shop-page onelive-item-shop-order-page">
        <OneliveSubPageLayout title={title}>
          <p className="onelive-sub-empty">상품을 찾을 수 없습니다.</p>
          <button type="button" className="btn btn-info" onClick={() => navigate('/shop')}>
            아이템샵으로
          </button>
        </OneliveSubPageLayout>
      </OnelivePageLayout>
    );
  }

  if (isChargeMode && (!Number.isFinite(parsedChargeAmount) || parsedChargeAmount < 1000)) {
    return (
      <OnelivePageLayout pageClassName="onelive-item-shop-page onelive-item-shop-order-page">
        <OneliveSubPageLayout title={title}>
          <p className="onelive-sub-empty">충전 금액이 올바르지 않습니다.</p>
          <button type="button" className="btn btn-info" onClick={() => navigate('/shop')}>
            아이템샵으로
          </button>
        </OneliveSubPageLayout>
      </OnelivePageLayout>
    );
  }

  return (
    <OnelivePageLayout pageClassName="onelive-item-shop-page onelive-item-shop-order-page">
      <OneliveSubPageLayout title={title}>
        {loading ? (
          <p className="onelive-loading">로딩 중...</p>
        ) : (
          <form className="item-order-form" onSubmit={onSubmit}>
            <div className="item-detail">
              <div className="proceeding-chat border-radius-none background-none">
                {!supportsCoinPayment ? (
                  <div>
                    <div className="pro-chat-tit background-white">
                      <h3>
                        <span><img src={oneliveAssets.orderItem} alt="" /></span>
                        {isChargeMode ? '충전 정보' : `아이템 정보 - ${productName}`}
                      </h3>
                    </div>
                    <div className="cash-box mar-cash background-white mar-t-5 pad-10">
                      <dl className="mar-t-15">
                        <dt>충전금액</dt>
                        <dd className="mony">{formatAmount(bankAmount)}</dd>
                      </dl>
                      <dl className="mar-t-15">
                        <dt>결제방식</dt>
                        <dd>{paymentGuide}</dd>
                      </dl>
                      {!isChargeMode ? (
                        <dl className="mar-t-15">
                          <dt>상세보기</dt>
                          <dd>{productDescription}</dd>
                        </dl>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="pro-chat-tit background-white onelive-pay-method-heading">
                        <h3>결제방법</h3>
                      </div>
                      <div className="giftcard onelive-pay-method-card">
                        <p className="onelive-pay-method-guide">원하시는 결제방식을 클릭하세요.</p>
                        <div className="radio-box onelive-pay-method-options">
                          <button
                            type="button"
                            className={payType === 'bank' ? 'active' : ''}
                            onClick={() => setPayType('bank')}
                          >
                            <span className="onelive-pay-method-icon">
                              <img src={oneliveAssets.orderBank} alt="" />
                            </span>
                            무통장입금
                          </button>
                          <button
                            type="button"
                            className={payType === 'coin' ? 'active' : ''}
                            onClick={() => setPayType('coin')}
                          >
                            <span className="onelive-pay-method-icon">
                              <img src={oneliveAssets.orderCoin} alt="" />
                            </span>
                            코인결제
                          </button>
                        </div>
                      </div>
                    </div>

                    {isCoinPayMode ? (
                      <div className="mar-t-15">
                        <div className="pro-chat-tit background-white">
                          <h3>
                            <span><img src={oneliveAssets.orderItem} alt="" /></span>
                            {`아이템 정보 - ${productName}`}
                          </h3>
                        </div>
                        <div className="cash-box mar-cash background-white mar-t-5 pad-10">
                          <dl className="mar-t-15">
                            <dt>코인차감</dt>
                            <dd className="mony">-{formatAmount(coinPayAmount)}</dd>
                          </dl>
                          <dl className="mar-t-15">
                            <dt>결제방식</dt>
                            <dd>{paymentGuide}</dd>
                          </dl>
                          <dl className="mar-t-15">
                            <dt>상세보기</dt>
                            <dd>{productDescription}</dd>
                          </dl>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}

                {!isCoinPayMode ? (
                  <div className="mar-t-15">
                    <div className="pro-chat-tit background-white">
                      <h3>
                        <span><img src={oneliveAssets.orderCoin} alt="" /></span>
                        합계금액
                      </h3>
                    </div>
                    <div className="cash-box mar-cash background-white pad-10">
                      <dl className="grid-two">
                        <dt>총 결제예정 금액</dt>
                        <dd className="mony text-right">{formatAmount(bankAmount)}</dd>
                      </dl>
                      <dl className="grid-two mar-t-10">
                        <dt>총 적립예정 코인</dt>
                        <dd className="mony text-right">{formatAmount(coinAmount)}</dd>
                      </dl>
                    </div>
                  </div>
                ) : null}

                <div className="mar-t-15">
                  {!isCoinPayMode ? (
                    <>
                      <div className="pro-chat-tit pad-lr-10 background-white">
                        <h3>
                          <span><img src={oneliveAssets.orderBank} alt="" /></span>
                          무통장입금
                        </h3>
                      </div>
                      <div className="giftcard">
                        <div id="pay_info_passbook" className="pay-info-passbook">
                          <p>
                            <span>입금자명:</span>
                            <input
                              name="depositor_name"
                              id="co_name"
                              style={{ width: 100 }}
                              value={depositorName}
                              onChange={(ev) => setDepositorName(ev.target.value)}
                              maxLength={100}
                              autoComplete="name"
                            />
                          </p>
                          <p>
                            <span>입금일시:</span>
                            <input
                              name="deposit_at"
                              id="co_datetime"
                              style={{ width: 180 }}
                              value={depositAt}
                              readOnly
                            />
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="pro-chat-tit pad-lr-10 background-white onelive-coin-pay-summary">
                      <h3>
                        <span><img src={oneliveAssets.orderBank} alt="" /></span>
                        코인결제 -{formatAmount(coinPayAmount)}
                      </h3>
                    </div>
                  )}

                  <button type="submit" className="btn button-orange" disabled={submitting}>
                    <img src={oneliveAssets.shopBuy} alt="" />
                    구매하기
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </OneliveSubPageLayout>
    </OnelivePageLayout>
  );
}
